import React, { forwardRef, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { DEFAULT_STYLES } from '../../constants';
import { ButtonProps } from '../../types';

// Loading Spinner Component
const LoadingSpinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={cn('animate-spin', className)}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="#475569"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export const ShadCNButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {
      type = 'submit',
      id,
      children,
      label,
      onClick,
      isLoading = false,
      isDisabled = false,
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      className,
      leftElement,
      rightElement,
      submitButtonSpinnerClassName,
      submitButtonLeftElementClassName,
      submitButtonContentClassName,
      submitButtonRightElementClassName,
      childrenClassName
    } = props;

    const isButtonDisabled = isDisabled || isLoading;
    const [isClicked, setIsClicked] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Get variant styles
    const getVariantStyles = () => {
      switch (variant) {
        case 'secondary':
          return DEFAULT_STYLES.buttonSecondary;
        case 'outline':
          return DEFAULT_STYLES.buttonOutline;
        case 'ghost':
          return DEFAULT_STYLES.buttonGhost;
        case 'primary':
        default:
          return DEFAULT_STYLES.buttonPrimary;
      }
    };

    // Get size styles
    const getSizeStyles = () => {
      switch (size) {
        case 'sm':
          return DEFAULT_STYLES.buttonSizeSm;
        case 'lg':
          return DEFAULT_STYLES.buttonSizeLg;
        case 'md':
        default:
          return DEFAULT_STYLES.buttonSizeMd;
      }
    };

    // Get spinner size based on button size
    const getSpinnerSize = () => {
      switch (size) {
        case 'sm':
          return 'w-3 h-3';
        case 'lg':
          return 'w-5 h-5';
        case 'md':
        default:
          return 'w-4 h-4';
      }
    };

    const buttonContent = children || label;

    // Handle click outside to remove clicked state
    useEffect(() => {
      if (!isClicked) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (
          buttonRef.current &&
          !buttonRef.current.contains(event.target as Node)
        ) {
          setIsClicked(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isClicked]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isButtonDisabled) {
        setIsClicked(true);
        if (onClick) {
          onClick(e);
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      // Handle keyboard navigation (Enter/Space)
      if ((e.key === 'Enter' || e.key === ' ') && !isButtonDisabled) {
        setIsClicked(true);
      }
    };

    // Combine refs using useImperativeHandle pattern
    React.useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(buttonRef.current);
        } else if ('current' in ref) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (ref as any).current = buttonRef.current;
        }
      }
    }, [ref]);

    return (
      <button
        ref={buttonRef}
        id={id}
        type={type}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={isButtonDisabled}
        aria-disabled={isButtonDisabled}
        aria-busy={isLoading}
        className={cn(
          DEFAULT_STYLES.button,
          getVariantStyles(),
          getSizeStyles(),
          fullWidth && DEFAULT_STYLES.buttonFullWidth,
          isButtonDisabled && DEFAULT_STYLES.buttonDisabled,
          isLoading && DEFAULT_STYLES.buttonLoading,
          isClicked && !isButtonDisabled && 'shadow-focus-ring-blue',
          className,
          'base__button'
        )}
      >
        {isLoading ? (
          <LoadingSpinner
            className={cn(
              getSpinnerSize(),
              buttonContent && 'mr-2',
              submitButtonSpinnerClassName
            )}
          />
        ) : (
          leftElement && (
            <span
              className={cn(
                'flex-shrink-0 flex items-center',
                submitButtonLeftElementClassName
              )}
            >
              {leftElement}
            </span>
          )
        )}
        {buttonContent && !isLoading && (
          <span
            className={cn(
              leftElement && !isLoading && 'ml-2',
              rightElement && !isLoading && 'mr-2',
              submitButtonContentClassName,
              childrenClassName
            )}
          >
            {buttonContent}
          </span>
        )}
        {!isLoading && rightElement && (
          <span
            className={cn(
              'flex-shrink-0 flex items-center',
              submitButtonRightElementClassName
            )}
          >
            {rightElement}
          </span>
        )}
      </button>
    );
  }
);

ShadCNButton.displayName = 'Button';
